import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatMoneyIntl } from "../../lib/formatMoney";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2 } from "lucide-react";
import { errMsg, weekdayLong } from "./calendarUtils";
import { DayWindowsRow } from "./DayWindowsRow";
import { ServiceCheckboxGroup } from "./ServiceCheckboxGroup";

// Backend keys of the weekly template, "0" = Monday … "6" = Sunday.
const DAY_KEYS = ["0", "1", "2", "3", "4", "5", "6"];
const emptyWeekly = () => DAY_KEYS.reduce((acc, k) => ({ ...acc, [k]: [] }), {});

// Mirrors `modules/booking/schemas.py` (MAX_BUFFER_MIN): the rest after an appointment is
// 0 (back-to-back) to 4 hours.
export const MAX_BUFFER_MIN = 240;

// Every IANA zone this browser knows, for the practice's time zone picker. The stored
// value is always listed even when this browser does not know it, so opening the editor
// never silently rewrites it.
const zoneOptions = (current) => {
  const known = typeof Intl.supportedValuesOf === "function"
    ? Intl.supportedValuesOf("timeZone") : [];
  return current && !known.includes(current) ? [current, ...known] : known;
};

// `template` may be null (no template created yet): the editor then starts a draft in the
// browser's own timezone — a first guess the practitioner can change below.
const normalize = (tpl) => ({
  timezone: tpl?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  // The TEMPLATE-LEVEL default set of services: every window that names none of its own
  // offers these (modules/booking/schemas.py's AvailabilityTemplateUpsert.service_ids).
  service_ids: tpl?.service_ids || [],
  // The rest after each appointment, in minutes. A booking occupies its service's
  // duration plus this, so the next offered start follows it; it may run past closing
  // time. Kept as typed (a string while editing) and validated on save.
  buffer_after_min: tpl?.buffer_after_min ?? 0,
  weekly: { ...emptyWeekly(), ...(tpl?.weekly || {}) },
  // horizon_days has no field on this screen but is required in the PUT body — carried
  // through from the loaded template, defaulting like the backend (schemas.py).
  horizon_days: tpl?.horizon_days ?? 60,
  status: tpl?.status || "draft",
});

// The shape a buffer must have before it may travel: a whole number of minutes in range.
const bufferIsValid = (value) => /^\d+$/.test(String(value).trim())
  && Number(value) <= MAX_BUFFER_MIN;

// What is wrong with one day, as the one sentence its row shows (or null). Mirrors the
// server's own checks, in the order a practitioner fixes them: the times themselves
// (end after start, no two windows over the same minutes), then open-hours decision A —
// a window that offers NO service, neither its own nor a default, has no length to offer
// and would publish hours nobody can book (422 WINDOW_WITHOUT_SERVICE).
const validateDay = (windows, defaultIds, hasCatalogue, t) => {
  for (const w of windows) {
    if (!w.start || !w.end || w.end <= w.start) return t("sched.windowInvalid");
  }
  const sorted = [...windows].sort((a, b) => a.start.localeCompare(b.start));
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].start < sorted[i - 1].end) return t("sched.windowOverlap");
  }
  if (!defaultIds.length && windows.some((w) => !(w.service_ids || []).length)) {
    return t(hasCatalogue ? "sched.windowNeedsService" : "sched.windowNeedsCatalogue");
  }
  return null;
};

/**
 * The weekly opening hours: when the practice is open, which services each window offers,
 * and the buffer after each appointment. Start times are NOT set here — they are computed
 * per service when a client looks (the service's own duration, back-to-back, then the
 * buffer), so there is no slot length and no capacity to configure.
 *
 * `services` is the practice's whole catalogue (the parent loads it once for this editor
 * and the extra-hours panel); only the active ones are offered as choices.
 */
export const WeeklyScheduleEditor = ({ template, connected, onSaved, services = [] }) => {
  const t = useT("admin");
  const { lang } = useLanguage();
  const initial = useMemo(() => normalize(template), [template]);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // The server's WINDOW_WITHOUT_SERVICE refusal names ONE day; it is shown on that row
  // until the draft changes (any edit may be the fix, and the next save re-asks).
  const [serverDayError, setServerDayError] = useState(null);

  const activeServices = useMemo(() => services.filter((s) => s.active), [services]);

  // The draft resyncs when the parent reloads the template (after a save or a publish).
  useEffect(() => { setDraft(initial); }, [initial]);
  useEffect(() => { setServerDayError(null); }, [draft]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);

  const dayErrors = useMemo(() => {
    const errs = {};
    DAY_KEYS.forEach((k) => {
      const e = validateDay(draft.weekly[k] || [], draft.service_ids, activeServices.length > 0, t);
      if (e) errs[k] = e;
    });
    if (serverDayError && !errs[serverDayError.day]) errs[serverDayError.day] = serverDayError.message;
    return errs;
  }, [draft.weekly, draft.service_ids, activeServices.length, serverDayError, t]);

  const bufferError = bufferIsValid(draft.buffer_after_min) ? null : t("sched.bufferRange", { max: MAX_BUFFER_MIN });
  const labels = weekdayLong(lang);

  const setDayWindows = (key) => (windows) => {
    setDraft((d) => ({ ...d, weekly: { ...d.weekly, [key]: windows } }));
  };

  const save = async () => {
    // The toast repeats the FIRST broken rule, in the words its field or row already
    // shows — never a fixed sentence that may name a rule the practitioner did not break.
    const firstError = bufferError || Object.values(dayErrors)[0];
    if (firstError) { toast.error(firstError); return; }
    setSaving(true);
    try {
      await api.put("/admin/availability-template", {
        timezone: draft.timezone,
        // The template-level default is always sent explicitly, unlike a window's own
        // set below: it has no "inherit from" to fall back to, so an empty set plainly
        // means "no default".
        service_ids: draft.service_ids,
        buffer_after_min: Number(draft.buffer_after_min),
        // The windows are NORMALISED on the way out — `{start, end}` plus `service_ids`
        // only where one was chosen — because the server refuses any other key (422), and
        // "inherit the template default" must reach it as an ABSENT key.
        weekly: Object.fromEntries(DAY_KEYS.map((k) => [
          k,
          (draft.weekly[k] || []).map((w) => ({
            start: w.start,
            end: w.end,
            ...(w.service_ids && w.service_ids.length ? { service_ids: w.service_ids } : {}),
          })),
        ])),
        horizon_days: Number(draft.horizon_days),
      });
      toast.success(t("sched.saved"));
      onSaved && onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "WINDOW_WITHOUT_SERVICE"
          && DAY_KEYS.includes(detail.day)) {
        setServerDayError({
          day: detail.day,
          message: t(activeServices.length ? "sched.windowNeedsService" : "sched.windowNeedsCatalogue"),
        });
      }
      if (detail && typeof detail === "object" && detail.code === "EXTRA_HOURS_OVERLAP"
          && DAY_KEYS.includes(detail.day)) {
        setServerDayError({
          day: detail.day,
          message: t("sched.extraHoursOverlap").replace("{date}", String(detail.date || "")),
        });
      }
      toast.error(errMsg(e, t, "sched.loadError"));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await api.post("/admin/availability-template/publish");
      toast.success(t("sched.published"));
      onSaved && onSaved();
    } catch (e) {
      toast.error(errMsg(e, t));
    } finally {
      setPublishing(false);
    }
  };

  const isPublished = draft.status === "published";

  // WHY PUBLISH CAN BE REFUSED, as ONE reason the tooltip repeats — never a silent
  // disabled button. Publishing makes the template the server has SAVED bookable, not the
  // draft on this screen: with unsaved edits the practitioner would publish the previous
  // version while reading the new one. So an unsaved draft blocks Publish until Save has
  // landed (which resyncs `initial` through `onSaved`, clearing `dirty`). The
  // calendar-connection precondition comes first because saving cannot lift it.
  const publishBlockedKey = !connected
    ? "sched.publishNeedsConnect"
    : dirty ? "sched.publishNeedsSave" : null;

  const serviceLabel = (s) => t("sched.serviceOption", {
    name: s.name,
    duration: s.duration_min,
    price: s.price_cents > 0 ? formatMoneyIntl(s.price_cents, s.currency, lang) : t("cal.priceFree"),
  });

  return (
    <div className="bg-eh-cream border border-eh-border-soft rounded-3xl p-6" data-testid="weekly-schedule-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="font-display text-xl text-eh-ink">{t("sched.settingsTitle")}</h2>
          <span
            className={`text-[11px] px-2 py-0.5 rounded-full ${isPublished ? "bg-eh-sage/20 text-eh-sage-deep" : "bg-eh-ochre/20 text-eh-ochre-deep"}`}
            data-testid="sched-status-badge"
          >
            {t(isPublished ? "sched.statusPublished" : "sched.statusDraft")}
          </span>
          {dirty && <span className="text-[11px] text-eh-muted italic" data-testid="sched-dirty">{t("sched.dirty")}</span>}
        </div>
        <div className="flex items-center gap-2">
          {/* `disabled` reflects only the in-flight request: a bad draft is refused at
              click time with the sentence its row or field already shows. */}
          <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid="sched-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("sched.save")}
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    onClick={publish}
                    disabled={publishing || publishBlockedKey !== null}
                    data-testid="sched-publish-btn"
                    className="bg-eh-ink text-eh-bg hover:opacity-90"
                  >
                    {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : t("sched.publish")}
                  </Button>
                </span>
              </TooltipTrigger>
              {publishBlockedKey && (
                <TooltipContent data-testid="sched-publish-blocked">{t(publishBlockedKey)}</TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          {/* THE PRACTICE'S zone, not the viewer's: the windows are wall-clock times in it,
              and every client sees them converted to their own. It used to be read-only
              and seeded from whichever browser saved first, so a practitioner travelling
              that day fixed the practice's hours in the wrong zone for ever. */}
          <Label htmlFor="sched-timezone" className="text-xs">{t("sched.timezone")}</Label>
          <select
            id="sched-timezone"
            value={draft.timezone}
            onChange={(e) => setDraft((d) => ({ ...d, timezone: e.target.value }))}
            className="mt-1 w-full h-10 rounded-md border border-input bg-white px-3 text-sm"
            data-testid="sched-timezone"
          >
            {zoneOptions(draft.timezone).map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
          <p className="text-[11px] text-eh-muted mt-1">{t("sched.timezoneHelp")}</p>
        </div>
        <div>
          {/* The TEMPLATE-LEVEL default: every window that ticks none of its own offers
              these. The names and prices are read from the catalogue; the length of an
              appointment is the service's own duration. */}
          <Label className="text-xs">{t("sched.templateServicesLabel")}</Label>
          {activeServices.length > 0 ? (
            <div className="mt-1">
              <ServiceCheckboxGroup
                services={activeServices}
                selectedIds={draft.service_ids}
                onChange={(ids) => setDraft((d) => ({ ...d, service_ids: ids }))}
                idPrefix="sched-service-checkboxes"
                renderLabel={serviceLabel}
              />
            </div>
          ) : (
            <p className="text-[11px] text-eh-muted mt-1" data-testid="sched-no-services">{t("sched.noServices")}</p>
          )}
        </div>
        <div>
          <Label className="text-xs" htmlFor="sched-buffer">{t("sched.buffer")}</Label>
          <Input
            id="sched-buffer"
            type="number"
            min="0"
            max={MAX_BUFFER_MIN}
            step="1"
            value={draft.buffer_after_min}
            onChange={(e) => setDraft((d) => ({ ...d, buffer_after_min: e.target.value }))}
            className={`mt-1 ${bufferError ? "border-red-500" : ""}`}
            aria-invalid={bufferError ? "true" : undefined}
            data-testid="sched-buffer"
          />
          {bufferError
            ? <p className="text-[11px] text-red-600 mt-1" role="alert" data-testid="sched-buffer-error">{bufferError}</p>
            : <p className="text-[11px] text-eh-muted mt-1">{t("sched.bufferHelp")}</p>}
        </div>
      </div>

      <div>
        {DAY_KEYS.map((k, i) => (
          <DayWindowsRow
            key={k}
            dayKey={k}
            label={labels[i]}
            windows={draft.weekly[k] || []}
            onChange={setDayWindows(k)}
            error={dayErrors[k]}
            services={activeServices}
          />
        ))}
      </div>
    </div>
  );
};
